import { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useHistoryStore } from '@/lib/store/history-store';
import type { SearchResult } from '@/lib/types';

/**
 * Real-time listener hook for deep search updates
 * Listens to Firestore for search progress and completion
 * @param userId - The user's unique ID
 * @param searchId - The search ID to listen to
 */
export function useDeepSearchListener(userId: string, searchId: string) {
  const updateProgress = useHistoryStore((state) => state.updateProgress);
  const updateSearch = useHistoryStore((state) => state.updateSearch);
  const addSearch = useHistoryStore((state) => state.addSearch);
  const getSearch = useHistoryStore((state) => state.getSearch);

  useEffect(() => {
    if (!userId || !searchId) {
      return;
    }

    const searchRef = doc(db, 'users', userId, 'searches', searchId);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      searchRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          console.warn('Search document does not exist:', searchId);
          return;
        }

        const searchData = snapshot.data();
        const existingSearch = getSearch(searchId);

        // If search doesn't exist in store, add it
        if (!existingSearch) {
          const newSearch: SearchResult = {
            id: searchId,
            type: searchData.type || 'person-search',
            query: searchData.query || '',
            status: searchData.status || 'analyzing',
            progress: searchData.progress || 0,
            timestamp: searchData.timestamp?.toDate?.() || new Date(),
            data: searchData.data,
            tokensUsed: searchData.tokensUsed,
            error: searchData.error,
          };
          addSearch(newSearch);
          return;
        }

        // Update progress in real-time
        if (searchData.progress !== undefined && searchData.status) {
          updateProgress(searchId, searchData.progress, searchData.status);
        }

        // If completed, update with full data
        if (searchData.status === 'completed' && searchData.data) {
          updateSearch(searchId, {
            status: 'completed',
            progress: 100,
            data: searchData.data,
            tokensUsed: searchData.tokensUsed,
          });
        }

        // Handle errors
        if (searchData.status === 'failed' && searchData.error) {
          updateSearch(searchId, {
            status: 'failed',
            error: {
              message: searchData.error.message || 'Search failed',
              code: searchData.error.code || 'UNKNOWN',
            },
          });
        }
      },
      (error) => {
        console.error('Search listener error:', error);
        // Update with error state if search exists in store
        const existingSearch = getSearch(searchId);
        if (existingSearch) {
          updateSearch(searchId, {
            status: 'failed',
            error: {
              message: 'Failed to listen for updates',
              code: 'LISTENER_ERROR',
            },
          });
        }
      }
    );

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [userId, searchId, updateSearch, updateProgress, addSearch, getSearch]);
}
